/**
 * The online queue (BUILD_PLAN section 8, DECISIONS.md #86, #87): choose a mech and a mode,
 * search, and get handed into the room the server assigned. The page polls the API once a
 * second while searching (the poll is also what keeps the ticket alive), resumes a search
 * after a reload, and offers the way back into a match that is still holding a seat.
 */
import { MECHS } from '@steelwake/shared/data';
import type { QueueStatus } from '@steelwake/shared/matchmaking';
import { HangarSchema, type HangarMech } from '@steelwake/shared/progression';
import { Button, colors, fonts, radii, spacing } from '@steelwake/ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';

import { api, ApiError, describeError } from '../../api.js';
import { reconnectStorageKey, ticketStorageKey, writeStored } from '../../net/params.js';
import { useAppStore } from '../../store.js';
import { MechPicker } from './MechPicker.js';

type Mode = 'casual' | 'ranked';

function storage(): Storage | null {
  return typeof sessionStorage === 'undefined' ? null : sessionStorage;
}

function describeQueueError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'EMAIL_UNVERIFIED') return 'Ranked play needs a verified email address.';
    if (err.code === 'QUEUE_TIMEOUT') {
      const retryAt = (err.details as { retryAt?: number } | undefined)?.retryAt;
      const when = retryAt ? new Date(retryAt).toLocaleTimeString() : 'later';
      return `You left a ranked match recently. Ranked opens again at ${when}.`;
    }
    if (err.code === 'ALREADY_QUEUED') return 'You are already searching.';
    if (err.code === 'IN_MATCH') return 'You already have a match: rejoin it first.';
    if (err.code === 'MECH_LOCKED') return 'Unlock that mech in the hangar first.';
    if (err.code === 'QUEUE_DISABLED') return 'The queue is closed for now; try again later.';
    if (err.code === 'RANKED_DISABLED') return 'Ranked play is paused; casual is open.';
  }
  return describeError(err);
}

const credits = (n: number) => n.toLocaleString('en-US');

export function Queue() {
  const user = useAppStore((s) => s.user);
  const navigate = useNavigate();
  const location = useLocation();
  const [mech, setMech] = useState(() => {
    // The hangar's Deploy button names the mech; anything unknown falls back to the default.
    const wanted = new URLSearchParams(window.location.search).get('mech');
    return wanted !== null && wanted in MECHS ? wanted : 'warden';
  });
  const [mode, setMode] = useState<Mode>('casual');
  const [allowBot, setAllowBot] = useState(true);
  const [status, setStatus] = useState<QueueStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** Ownership from the hangar; until it arrives every mech is offered and the API decides. */
  const [owned, setOwned] = useState<Map<string, HangarMech> | null>(null);
  const handedOff = useRef(false);

  useEffect(() => {
    let cancelled = false;
    api<unknown>('/api/v1/hangar')
      .then((body) => {
        const hangar = HangarSchema.parse(body);
        if (!cancelled) setOwned(new Map(hangar.mechs.map((m) => [m.id, m])));
      })
      .catch(() => {
        // The queue still works without the badges; the API refuses a locked mech anyway.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const poll = useCallback(async () => {
    try {
      const next = await api<QueueStatus>('/api/v1/match/status');
      setStatus(next);
      return next;
    } catch (err) {
      setError(describeQueueError(err));
      return null;
    }
  }, []);

  // A reload mid-search (or a closed tab mid-match) lands back where it was.
  useEffect(() => {
    void poll();
  }, [poll]);

  useEffect(() => {
    if (status?.state !== 'searching') return;
    const timer = setInterval(() => void poll(), 1000);
    return () => clearInterval(timer);
  }, [status?.state, poll]);

  // The worker assigned a room: keep the ticket for the match page and go.
  useEffect(() => {
    if (status?.state !== 'matched' || handedOff.current) return;
    handedOff.current = true;
    writeStored(storage(), ticketStorageKey(status.roomId), status.ticket);
    void navigate(matchUrl(status.roomId, location.search));
  }, [status, navigate, location.search]);

  const find = async () => {
    setBusy(true);
    setError(null);
    try {
      setStatus(
        await api<QueueStatus>('/api/v1/match/queue', {
          method: 'POST',
          body: { mode, mechId: mech, allowBot },
        }),
      );
    } catch (err) {
      setError(describeQueueError(err));
      if (err instanceof ApiError && (err.code === 'ALREADY_QUEUED' || err.code === 'IN_MATCH')) {
        await poll();
      }
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    setError(null);
    try {
      setStatus(await api<QueueStatus>('/api/v1/match/queue/leave', { method: 'POST' }));
    } catch (err) {
      // Pairing in progress: the next poll says where to go.
      if (!(err instanceof ApiError && err.code === 'PAIRING')) setError(describeQueueError(err));
      await poll();
    } finally {
      setBusy(false);
    }
  };

  const rejoin = (roomId: string, reconnectionToken: string | null) => {
    writeStored(storage(), reconnectStorageKey(roomId), reconnectionToken);
    void navigate(matchUrl(roomId, location.search));
  };

  if (status?.state === 'inMatch') {
    return (
      <Panel testId="queue-in-match">
        <h1 style={h1}>Your match is still running</h1>
        <p style={{ color: colors.textMuted, fontSize: 14 }}>
          The server is holding your seat. Rejoin now, or the match is forfeited when the grace
          period ends.
        </p>
        <Button
          data-testid="rejoin-match"
          onClick={() => rejoin(status.roomId, status.reconnectionToken)}
        >
          Rejoin match
        </Button>
      </Panel>
    );
  }

  if (status?.state === 'searching') {
    const waited = Math.round(status.waitSeconds);
    return (
      <Panel testId="queue-searching">
        <h1 style={h1}>Searching for an opponent</h1>
        <p data-testid="queue-wait" style={{ fontSize: 14 }}>
          {status.mode === 'ranked' ? 'Ranked' : 'Casual'} · {String(waited)} s
          {status.band !== null ? ` · rating band ±${String(status.band)}` : ''}
          {status.allowBot ? ' · a bot fills in if nobody comes' : ''}
        </p>
        {error ? (
          <p data-testid="queue-error" style={{ color: colors.danger, fontSize: 13 }}>
            {error}
          </p>
        ) : null}
        <Button
          variant="ghost"
          data-testid="cancel-queue"
          onClick={() => void cancel()}
          disabled={busy}
        >
          Cancel
        </Button>
      </Panel>
    );
  }

  if (status?.state === 'matched') {
    return (
      <Panel testId="queue-matched">
        <h1 style={h1}>Match found</h1>
        <p style={{ fontSize: 14 }}>
          Against {status.opponent.callsign}
          {status.opponent.bot ? ' (bot)' : ''} · loading the arena…
        </p>
      </Panel>
    );
  }

  const penalty = status?.state === 'idle' ? status.penaltyUntil : null;
  const ranked = mode === 'ranked';
  const locked = owned !== null && !(owned.get(mech)?.owned ?? true);
  return (
    <MechPicker
      testId="queue"
      value={mech}
      onChange={setMech}
      title="Play online · choose your mech"
      intro={`Signed in as ${user?.callsign ?? user?.email ?? 'pilot'}. Casual takes any account; ranked needs a verified email. Credits, XP and rating are recorded when the match ends.`}
      cardNote={(m) => {
        const entry = owned?.get(m.id);
        if (!entry) return m.starter ? <span style={{ color: colors.accent }}>starter</span> : null;
        if (entry.starter) return <span style={{ color: colors.accent }}>starter</span>;
        if (entry.owned) return <span style={{ color: colors.success }}>owned</span>;
        return (
          <span style={{ color: colors.textMuted }}>
            locked{entry.price !== null ? ` · ${credits(entry.price)} cr` : ''}
          </span>
        );
      }}
      footer={
        <>
          {locked ? (
            <p
              data-testid="queue-locked"
              style={{ color: colors.warning, fontSize: 13, margin: `0 0 ${spacing.sm}` }}
            >
              Unlock {MECHS[mech]?.name ?? mech} in the <Link to="/hangar">hangar</Link> first.
            </p>
          ) : null}
          <div style={{ fontSize: 13, color: colors.textMuted, marginBottom: 4 }}>Mode</div>
          <div style={{ display: 'flex', gap: spacing.sm, marginBottom: spacing.md }}>
            {(['casual', 'ranked'] as const).map((m) => (
              <Button
                key={m}
                variant={mode === m ? 'primary' : 'ghost'}
                aria-pressed={mode === m}
                data-testid={`queue-mode-${m}`}
                onClick={() => setMode(m)}
              >
                {m}
              </Button>
            ))}
          </div>
          {ranked && user && !user.emailVerified ? (
            <p style={{ color: colors.warning, fontSize: 13, margin: `0 0 ${spacing.sm}` }}>
              Ranked needs a verified email address.
            </p>
          ) : null}
          {penalty !== null && penalty > Date.now() ? (
            <p
              data-testid="queue-penalty"
              style={{ color: colors.warning, fontSize: 13, margin: `0 0 ${spacing.sm}` }}
            >
              Ranked opens again at {new Date(penalty).toLocaleTimeString()} (you left a ranked
              match).
            </p>
          ) : null}
          <label style={{ display: 'flex', gap: 8, fontSize: 13, marginBottom: spacing.md }}>
            <input
              type="checkbox"
              data-testid="queue-allow-bot"
              checked={allowBot}
              onChange={(e) => setAllowBot(e.target.checked)}
            />
            <span>Fill with a bot if nobody is found in a minute</span>
          </label>
          {error ? (
            <p data-testid="queue-error" style={{ color: colors.danger, fontSize: 13 }}>
              {error}
            </p>
          ) : null}
          <Button data-testid="find-match" onClick={() => void find()} disabled={busy || locked}>
            Find match
          </Button>
        </>
      }
    />
  );
}

const h1 = { margin: `0 0 ${spacing.sm}`, fontSize: 22, letterSpacing: '0.12em' } as const;

function Panel({ children, testId }: { children: React.ReactNode; testId: string }) {
  return (
    <div
      data-testid={testId}
      style={{
        width: 'min(560px, 96vw)',
        fontFamily: fonts.ui,
        color: colors.text,
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: radii.md,
        padding: spacing.lg,
      }}
    >
      {children}
    </div>
  );
}

/** The match address: the room, plus whatever else the queue address carried (e2e, preset…). */
function matchUrl(roomId: string, search: string): string {
  const rest = new URLSearchParams(search);
  rest.delete('room');
  rest.delete('ticket');
  const qs = rest.toString();
  return `/play/match?room=${encodeURIComponent(roomId)}${qs ? `&${qs}` : ''}`;
}
